import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { fabric } from 'fabric';
import { ToastVariants } from '@dotcom/crown-ui';
import { useCloseFontDrawer } from '../../../components/card-controls/font-drawer/hooks/use-close-font-drawer';
import { useAnalyticsContext } from '../../../context/analytics-context/analytics-context';
import { hideLoadingScreen, setIsToasterOpen } from '../../../context/app-context';
import { useAppContext } from '../../../context/app-context/app-context';
import { storePreviews } from '../../../context/app-context/app-context-actions';
import { useCardContext } from '../../../context/card-context';
import { useInitializationDataContext } from '../../../context/data-context';
import { addOnGoingEvent, useInitializationOnGoingEventsContext } from '../../../context/on-going-events-context';
import { resetOnGoingEvents } from '../../../context/on-going-events-context/on-going-events-context-actions';
import { useActiveCanvas, useFeatureFlags, useIsDigitalGreeting } from '../../../hooks';
import { useSaveProject } from '../../../hooks/use-save-project';
import { config } from '../../../regional-config';
import { CanvasDataTypes } from '../../../utils';
import { highlightMustEditText } from '../../../utils/utility';
import { Zone } from '../editor-types';
import { getMandatoryEditTextAreas } from '../utils/get-mandatory-edit-text-areas';

/**
 * useHandleProjectDone is a hook that handles the completion of a card project.
 * It checks if there are fields that need to be completed before moving to the next view.
 *
 * @param skipValidation - A ref object that indicates whether to skip validation.
 * @param validateUserZones - A function that checks if the user has edited the necessary zones.
 * @param uneditedZones - An array of zones that haven't been edited.
 * @param isOneToMany - A boolean indicating whether the card is 1:Many type.
 * @param handleIndexChange - A function that handles the changing of the Card Face.
 *
 * @returns A function that can be invoked when the card project is done.
 */
export const useHandleProjectDone = (
  skipValidation: React.MutableRefObject<boolean>,
  validateUserZones: () => boolean,
  uneditedZones: Zone[],
  isOneToMany: boolean,
  handleIndexChange: (newIndex: number, newActiveCardIndex?: number) => void,
  ignoreSpellcheckValidation: React.MutableRefObject<boolean>,
) => {
  // Initialize hooks and variables
  const { t } = useTranslation();
  const { saveProject } = useSaveProject();
  const {
    appState: { previewImages },
    appDispatch,
  } = useAppContext();
  const {
    trackClickAddressRecipient,
    trackUneditedZoneError,
    trackEmailDeliveryRecipient,
    trackUneditedTextBoxWarningTag,
  } = useAnalyticsContext();
  const {
    onGoingEventsState: { onGoingEvents },
    onGoingEventsDispatch,
  } = useInitializationOnGoingEventsContext();
  const { IS_ECARD_SEND_ENABLED, IS_ENVELOPE_PRINTING_OPTIONS_ENABLED } = useFeatureFlags();
  const isDigitalGreeting = useIsDigitalGreeting();
  const { search } = useLocation();
  const navigate = useNavigate();
  const {
    cardState: { cardFacesList },
  } = useCardContext();
  const {
    initializedDataState: { data: initializedData },
  } = useInitializationDataContext();
  const projectTypeCode = initializedData?.project_type_code;
  const canvas = useActiveCanvas();
  const { onFontDrawerSubmit } = useCloseFontDrawer();

  const {
    cardEditor: { lastCardFaceRoute },
  } = config;

  const isEnvelopeSelectionEnabled = IS_ENVELOPE_PRINTING_OPTIONS_ENABLED && isOneToMany;

  return useCallback(() => {
    // if the font drawer is open, close it so it can save current edits
    onFontDrawerSubmit(canvas);

    const forEachCardFaceCanvas = (callback: (faceCanvas: fabric.Canvas) => void) => {
      cardFacesList.forEach((face) => {
        const faceCanvas = face?.canvas.current;

        if (faceCanvas) {
          callback(faceCanvas);
        }
      });
    };

    const getFirstRequiredFieldFaceIndexes = (fieldsToBeEdited: Zone[]) => {
      const firstRequiredFieldFaceType = fieldsToBeEdited[0]?.face;
      const editableFaces = cardFacesList.filter((face) => face.editorDisplayIndicator);
      const targetSlideIndex = editableFaces.findIndex((face) => face.type === firstRequiredFieldFaceType);
      const targetFace = editableFaces.find((face) => face.type === firstRequiredFieldFaceType);

      if (targetSlideIndex < 0 || !targetFace) {
        return { activeCardIndex: 0, slideIndex: 0 };
      }

      return { activeCardIndex: targetFace.faceNumber - 1, slideIndex: targetSlideIndex };
    };

    const showErrorToastAndSetSkipValidation = (
      titleKey: string,
      descriptionKey: string,
      shouldSkip: boolean,
      slideIndexes?: { slideIndex: number; activeCardIndex: number },
    ) => {
      if (slideIndexes) {
        handleIndexChange(slideIndexes.slideIndex, slideIndexes.activeCardIndex);
      }
      setIsToasterOpen(appDispatch, {
        variant: ToastVariants.Error,
        title: t(titleKey),
        children: t(descriptionKey),
      });
      skipValidation.current = shouldSkip;
    };

    // Spellcheck is disabled - validation skipped

    /**
     * Sends one analytics event per card face holding zones that block the user from moving forward,
     * describing whether that face is missing text, an image, or both.
     */
    const trackBlockingZones = (blockingZones: Zone[], statusMessage: string) => {
      if (blockingZones.length === 0) {
        return;
      }

      const zonesByFace = new Map<string, Zone[]>();
      blockingZones.forEach((zone) => {
        zonesByFace.set(zone.face, [...(zonesByFace.get(zone.face) ?? []), zone]);
      });

      addOnGoingEvent(onGoingEventsDispatch, statusMessage);
      zonesByFace.forEach((faceZones, face) => {
        const textZone = faceZones.find(({ zone }) => zone.data?.type === CanvasDataTypes.EditableText);
        const photoZone = faceZones.find(({ zone }) => zone.data?.type === CanvasDataTypes.PhotoZone);
        const primaryZone = textZone ?? photoZone ?? faceZones[0];
        const secondaryZone = primaryZone === textZone ? photoZone : undefined;

        trackUneditedZoneError(
          statusMessage,
          primaryZone.zone as fabric.Object,
          face,
          secondaryZone?.zone as fabric.Object | undefined,
        );
      });
    };

    // Check if there are any fields that must be edited (MustEditTextArea: true)
    const fieldsToBeEdited = getMandatoryEditTextAreas(uneditedZones, cardFacesList);

    // If there are fields to be edited, show an error toast and set skipValidation to false
    if (fieldsToBeEdited.length > 0) {
      const statusMessage = `${t('fieldsToBeEditedError.toastErrorTitle')} | ${t(
        'fieldsToBeEditedError.toastErrorDescription',
      )}`;

      trackBlockingZones(fieldsToBeEdited, statusMessage);

      const { activeCardIndex, slideIndex } = getFirstRequiredFieldFaceIndexes(fieldsToBeEdited);

      forEachCardFaceCanvas((faceCanvas) => {
        faceCanvas.on('after:render', () => highlightMustEditText(faceCanvas));
      });
      showErrorToastAndSetSkipValidation(
        'fieldsToBeEditedError.toastErrorTitle',
        'fieldsToBeEditedError.toastErrorDescription',
        false,
        { slideIndex, activeCardIndex },
      );
      forEachCardFaceCanvas((faceCanvas) => faceCanvas.requestRenderAll());
      return;
    }

    const hasIncompleteUserZoneWarning = !validateUserZones() && !skipValidation.current;

    // If user zones do not validate and skipValidation is false, show an error toast and set skipValidation to true
    if (hasIncompleteUserZoneWarning) {
      const statusMessage = `${t('editableFieldsError.toastErrorTitle')} | ${t(
        'editableFieldsError.toastErrorDescription',
      )}`;

      // `validateUserZones` only blocks on the zones it validates: every zone for Sign & Send projects,
      // and the front face zones for every other project type.
      const blockingUserZones =
        projectTypeCode === 'S' ? uneditedZones : uneditedZones.filter(({ face }) => face === 'front');

      trackBlockingZones(blockingUserZones, statusMessage);

      showErrorToastAndSetSkipValidation(
        'editableFieldsError.toastErrorTitle',
        'editableFieldsError.toastErrorDescription',
        true,
        { slideIndex: 0, activeCardIndex: 0 },
      );
      return;
    }

    // Otherwise, save the project and go to the next view
    // Remove highlight from texts so it isn't included in the saved personalization json - spellcheck disabled
    // only execute if executed a previous error toast and was added on going events
    if (onGoingEvents.length > 0) {
      trackUneditedTextBoxWarningTag(onGoingEvents);
    }
    if (previewImages.length > 0) {
      storePreviews(appDispatch, []); // todo: discuss with the team, since this stores nothing
    }
    saveProject({
      shouldRestoreCanvas: false,
      showLoadingScreen: true,
      generateFrontPreview: false,
      generateFEPreviews: true,
    })
      ?.then(() => {
        if (isDigitalGreeting) {
          trackEmailDeliveryRecipient();
        } else {
          trackClickAddressRecipient();
        }
        // sets default value to the envelope if the card is 1:Many
        const searchParams = new URLSearchParams(search);
        const nextRoute =
          isDigitalGreeting && IS_ECARD_SEND_ENABLED
            ? 'ecard-send'
            : isEnvelopeSelectionEnabled
            ? 'envelope'
            : lastCardFaceRoute;
        // Push to next view
        navigate({
          pathname: `/card/customization/${nextRoute}`,
          search: searchParams.toString(),
        });
        hideLoadingScreen(appDispatch);
        // Reset on-going events to empty array once the project is saved
        resetOnGoingEvents(onGoingEventsDispatch);
      })
      .catch(() => {
        // saveProject already surfaced the error toast and reset the loading overlay.
        // Swallow here so the user stays on the editor (no navigation, no unhandled rejection).
      });
  }, [validateUserZones, skipValidation.current, isOneToMany, ignoreSpellcheckValidation.current]);
};
